import React, { Fragment, useCallback, useEffect, useState, useContext } from 'react';
import '../Components/modal.css';
import './AddDevice.css';
import axios from 'axios';
import { BASE_URL_API, STATIC_ICONS } from '../Constants';
import { useAuth } from '../hooks/AuthProvider';
import Select from 'react-select';
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Alert from '../Components/Alert';
import { textFieldSlotProps } from './../Style';

import { LanguageContext } from '../context/LanguageContext';
import devTranslations from '../languages/device.json';
import { getErrorMessage } from '../utils/errorUtils';
import { useFontSize } from '../context/FontSizeContext';
import {  DeviceIdIcon } from '../svg/DeviceIcons';
import { NameIcon, AddNewIcon } from '../svg/UtilsIcons';
import { TemplateIcon } from '../svg/TemplateIcons';
import { OrgNameIcon } from '../svg/OrganizationIcons';
import { normalizeLocationListRow, normalizeTemplateListRow } from '../utils/listRowIds';

const parseOptionalNumberField = (value) => {
    const normalized = String(value ?? '').trim();
    if (!normalized) return { value: undefined };

    const parsed = Number(normalized);
    return Number.isFinite(parsed)
        ? { value: parsed }
        : { error: true };
};

export const AddDevice = ({ refresh, setState, fixedTemplate = null }) => {
    const { user, org, permissions } = useAuth();
    const { lang } = useContext(LanguageContext);
    useFontSize();

    const canProvisionDevices = permissions['provision_new_devices'];
    const canViewLocations = permissions['view_locations'];
    const isSuperUser = String(user?.role || '').toLowerCase() === 'super';

    const tRoot = devTranslations[lang] || devTranslations.en;
    const t = tRoot.addDevice;
    const isRtl = ['ar', 'ur'].includes(lang);

    const [formData, setFormData] = useState({
        template: fixedTemplate ? {
            id: fixedTemplate.id,
            tid: fixedTemplate.tid,
            name: fixedTemplate.name,
            icon: fixedTemplate.icon
        } : null,
        name: '',
        did: '',
        locationId: null,
        room_size: '',
        coverage_area: '',
        cfm: '',
        ac_capacity: '',
        light_intensity: '',
        duration_plan: '',
        expired_at: '',
        plan_request_note: ''
    });

    const [templates, setTemplates] = useState([]);
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = useCallback((type, message) => {
        setAlertState({
            open: true,
            type,
            message,
            position: isRtl ? 'left' : 'right'
        });
    }, [isRtl]);

    useEffect(() => {
        if (!fixedTemplate) return;
        setFormData((prev) => ({
            ...prev,
            template: {
                id: fixedTemplate.id,
                tid: fixedTemplate.tid,
                name: fixedTemplate.name,
                icon: fixedTemplate.icon
            }
        }));
    }, [fixedTemplate]);

    useEffect(() => {
        if (!canProvisionDevices) return;

        const fetchTemplates = async () => {
            try {
                const res = await axios.get(`${BASE_URL_API}/template/list`);

                if (res.data && res.data.status === 'OK') {
                    const list = Array.isArray(res.data.data)
                        ? res.data.data.map(normalizeTemplateListRow)
                        : [];
                    setTemplates(list);

                    if (fixedTemplate?.id) {
                        const found = list.find((x) => Number(x.id) === Number(fixedTemplate.id));
                        if (found) {
                            setFormData((prev) => ({ ...prev, template: found }));
                        }
                    }
                } else {
                    throw new Error(
                        'Received an invalid response from the server.'
                    );
                }
            } catch (err) {
                console.error('Failed to fetch templates:', err);
                const errorMessage = getErrorMessage(err);
                showAlert('error', `${t.loadTemplatesError}: ${errorMessage}`);
                setTemplates([]);
            }
        };

        fetchTemplates();
    }, [canProvisionDevices, fixedTemplate?.id, showAlert, t.loadTemplatesError]);

    useEffect(() => {
        if (!canProvisionDevices || !canViewLocations) {
            setLocations([]);
            return;
        }

        let mounted = true;

        const fetchLocations = async () => {
            setLocationsLoading(true);
            try {
                const res = await axios.get(`${BASE_URL_API}/location`);
                const list = Array.isArray(res?.data?.data)
                    ? res.data.data.map(normalizeLocationListRow)
                    : [];

                if (!mounted) return;
                setLocations(list);
            } catch (err) {
                if (!mounted) return;
                const errorMessage = getErrorMessage(err);
                showAlert('error', `${t.loadLocationsError}: ${errorMessage}`);
                setLocations([]);
            } finally {
                if (mounted) {
                    setLocationsLoading(false);
                }
            }
        };

        fetchLocations();

        return () => {
            mounted = false;
        };
    }, [canProvisionDevices, canViewLocations, showAlert, t.loadLocationsError]);

    const createDevice = async () => {
        if (!formData.name || formData.name.trim() === '') {
            showAlert('error', t.validation.nameRequired);
            return;
        }
        if (!formData.did || formData.did.trim() === '') {
            showAlert('error', t.validation.didRequired);
            return;
        }
        if (!formData.template?.id) {
            showAlert('error', t.validation.templateRequired);
            return;
        }
        if (formData.expired_at) {
            const expiredAtDate = dayjs(formData.expired_at);
            if (!expiredAtDate.isValid() || expiredAtDate.isBefore(dayjs())) {
                showAlert('error', t.validation.expiredAtFuture);
                return;
            }
        }

        const parsedCfm = parseOptionalNumberField(formData.cfm);
        if (parsedCfm.error) {
            showAlert('error', t.validation?.cfmNumber || 'CFM must be a valid number.');
            return;
        }

        const parsedAcCapacity = parseOptionalNumberField(formData.ac_capacity);
        if (parsedAcCapacity.error) {
            showAlert('error', t.validation?.acCapacityNumber || 'AC Capacity must be a valid number.');
            return;
        }

        const body = {
            name: formData.name.trim(),
            did: formData.did.trim(),
            location_id: formData.locationId || null,
            room_size: formData.room_size?.trim() || undefined,
            coverage_area: formData.coverage_area?.trim() || undefined,
            cfm: parsedCfm.value,
            ac_capacity: parsedAcCapacity.value,
            light_intensity: formData.light_intensity?.trim() || undefined,
            template_id: formData.template.id,
            tid: formData.template.tid,
            template_name: formData.template?.name,
            organization_id: org.id,
            organization_name: org.name,
            owner_id: user.id,
            owner_name: user.name,
            icon: formData.template?.icon,
            duration_plan: formData.duration_plan ? Number(formData.duration_plan) : undefined,
            expired_at: formData.expired_at ? new Date(formData.expired_at).toISOString() : undefined,
            plan_request_note: formData.plan_request_note?.trim() || undefined
        };

        try {
            const res = await axios.post(
                `${BASE_URL_API}/device/create`,
                body,
                { headers: { 'Content-Type': 'application/json' } }
            );

            showAlert(
                'success',
                res.data.message || t.createSuccess
            );

            refresh();

            setTimeout(() => {
                setState(false);
            }, 2000);
        } catch (err) {
            let errorMessage = getErrorMessage(err);
            if (
                err.response?.data?.errors &&
                Array.isArray(err.response.data.errors)
            ) {
                errorMessage = err.response.data.errors
                    .map(e => e.msg)
                    .join(' ');
            }

            showAlert('error', errorMessage);
        }
    };

    if (!canProvisionDevices) {
        return (
            <div
                id="modal"
                style={{
                    direction:
                        lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
                }}
            >
                <div
                    id="popup"
                    className="dashboard-modal tall"
                    style={{
                        animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))'
                    }}
                >
                    <div className="dashboard-header">
                        <div className="dashboard-header-icon">
                            <AddNewIcon />
                        </div>
                        <div className="dashboard-header-text">
                            <h2>
                                {t.permissionDenied.title}
                            </h2>
                            <p>
                                {t.permissionDenied.message}
                            </p>
                        </div>
                    </div>


                    <div className="modal-content dashboard-content" />

                    <div className="modal-footer dashboard-footer">
                        <button
                            className="btn btn-cancel"
                            onClick={() => setState(false)}
                        >
                            {t.buttonClose}
                        </button>
                        <div />
                    </div>
                </div>
            </div>
        );
    }

    const templateOptionsRaw = Array.isArray(templates) ? templates : [];

    const fixedTemplateOption = fixedTemplate?.id
        ? {
              value: fixedTemplate.id,
              label: fixedTemplate.name,
              id: fixedTemplate.id,
              tid: fixedTemplate.tid,
              name: fixedTemplate.name,
              icon: fixedTemplate.icon,
          }
        : null;

    const templateOptions = [
        ...(fixedTemplateOption && !templateOptionsRaw.some((x) => Number(x.id) === Number(fixedTemplate.id))
            ? [fixedTemplateOption]
            : []),
        ...templateOptionsRaw.map((template) => ({
            value: template.id,
            label: template.name,
            ...template,
        })),
    ];

    const selectedTemplateOption = formData.template
        ? templateOptions.find(
              option => Number(option.value) === Number(formData.template.id)
          )
        : null;
    const locationOptions = (Array.isArray(locations) ? locations : []).map((location) => ({
        value: location.id,
        label: location.address
            ? `${location.name || t.locationOptionFallback} (${location.address})`
            : (location.name || t.locationOptionFallbackWithId.replace('{id}', location.id)),
        ...location,
    }));
    const selectedLocationOption = locationOptions.find(
        (option) => Number(option.value) === Number(formData.locationId)
    ) || null;

    const expiredAtValue = formData.expired_at
        ? dayjs(formData.expired_at)
        : null;

    const expiredAtSlotProps = {
        ...(textFieldSlotProps || {}),
        textField: {
            ...((textFieldSlotProps && textFieldSlotProps.textField) ? textFieldSlotProps.textField : {}),
            className: 'add-device-expired-at-field',
            fullWidth: true,
            placeholder: t.placeholderExpiredAt,
        },
        popper: {
            ...((textFieldSlotProps && textFieldSlotProps.popper) ? textFieldSlotProps.popper : {}),
            disablePortal: true,
            placement: isRtl ? 'bottom-start' : 'bottom-end',
            className: 'add-device-expired-at-popper',
        },
        desktopPaper: {
            className: 'add-device-expired-at-paper',
        },
        dialog: {
            className: 'add-device-expired-at-dialog',
        },
    };

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--rs-control-bg)',
    borderColor: state.isFocused
      ? 'var(--rs-control-border-focus)'
      : 'var(--rs-control-border)',
    boxShadow: state.isFocused ? 'var(--rs-control-focus-shadow)' : 'none',
    '&:hover': {
      borderColor: 'var(--rs-control-border-hover)',
    },
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    minHeight: '44px',
    borderRadius: 8,
    cursor: 'pointer',
  }),

  singleValue: (provided) => ({
    ...provided,
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  input: (provided) => ({
    ...provided,
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  placeholder: (provided) => ({
    ...provided,
    color: 'var(--rs-placeholder)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--rs-menu-bg)',
    border: '1px solid var(--rs-menu-border)',
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: 9999,
  }),

  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--rs-option-selected-bg)'
      : state.isFocused
      ? 'var(--rs-option-hover-bg)'
      : 'var(--rs-option-bg)',
    color: state.isSelected ? 'var(--rs-option-selected-text)' : 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    '&:active': {
      backgroundColor: 'var(--rs-option-active-bg)',
    },
    cursor: 'pointer',
  }),

  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'var(--rs-indicator)',
    paddingRight: 12,
    '&:hover': {
      color: 'var(--rs-indicator-hover)',
    },
  }),

  clearIndicator: (provided) => ({
    ...provided,
    color: 'var(--rs-indicator)',
    '&:hover': {
      color: 'var(--rs-indicator-hover)',
    },
  }),

  indicatorSeparator: () => ({
    display: 'none',
  }),
};


    return (
        <div
            id="modal"
            style={{
                direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
            }}
        >
            <Alert
                open={alertState.open}
                close={() =>
                    setAlertState({ ...alertState, open: false })
                }
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />
            <div
                id="popup"
                className="dashboard-modal"
                style={{
                    animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                    fontSize:
                        'calc(var(--body-font-size) * var(--font-size-multiplier))'
                }}
            >
                <div className="dashboard-header">
                    <div className="dashboard-header-icon">
                        <AddNewIcon />
                    </div>
                    <div className="dashboard-header-text">
                        <h2>{t.title}</h2>
                        <p>{t.description}</p>
                    </div>
                </div>

                <div className="modal-divider" />

                <div className="modal-content dashboard-content">
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <NameIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelDeviceName}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="field-with-counter">
                                <input
                                    type="text"
                                    className="add-column"
                                    placeholder={t.placeholderName}
                                    maxLength={50}
                                    value={formData.name}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            name: e.target.value
                                        })
                                    }
                                />
                                <span className="field-counter">
                                    {formData.name?.length || 0} / 50
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <DeviceIdIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelDeviceID}
                            </span>
                        </div>

                        <div className="form-item">
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.placeholderDeviceID}
                                value={formData.did}
                                onKeyDown={e => {
                                    if (e.key === ' ') e.preventDefault();
                                }}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        did: e.target.value.replace(/\s+/g, '')
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.labelRoomSize}
                            </span>
                        </div>

                        <div className="form-item">
                            <input
                                type="text"
                                className="add-column"
                                maxLength={255}
                                placeholder={t.placeholderRoomSize}
                                value={formData.room_size}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        room_size: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.labelCoverageArea}
                            </span>
                        </div>

                        <div className="form-item">
                            <input
                                type="text"
                                className="add-column"
                                maxLength={255}
                                placeholder={t.placeholderCoverageArea}
                                value={formData.coverage_area}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        coverage_area: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.labelCfm || 'CFM'}
                            </span>
                        </div>

                        <div className="form-item">
                            <input
                                type="number"
                                className="add-column"
                                step="any"
                                placeholder={t.placeholderCfm || 'Optional CFM'}
                                value={formData.cfm}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        cfm: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.labelAcCapacity || 'AC Capacity'}
                            </span>
                        </div>

                        <div className="form-item">
                            <input
                                type="number"
                                className="add-column"
                                step="any"
                                placeholder={t.placeholderAcCapacity || 'Optional AC capacity'}
                                value={formData.ac_capacity}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        ac_capacity: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.labelLightIntensity}
                            </span>
                        </div>

                        <div className="form-item">
                            <input
                                type="text"
                                className="add-column"
                                maxLength={255}
                                placeholder={t.placeholderLightIntensity}
                                value={formData.light_intensity}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        light_intensity: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <TemplateIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelTemplate}
                            </span>
                        </div>

                        <div className="form-item">
                            <Select
                                id="template"
                                options={templateOptions}
                                value={selectedTemplateOption}
                                onChange={selectedOption =>
                                    setFormData(prev => ({
                                        ...prev,
                                        template: selectedOption || null
                                    }))
                                }
                                styles={customStyles}
                                placeholder={t.placeholderTemplate}
                                isClearable
                                isDisabled={Boolean(fixedTemplate?.id)}
                            />
                        </div>
                    </div>

                    {canViewLocations && (
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-title-text">
                                    {t.labelLocation || 'Location'}
                                </span>
                            </div>

                            <div className="form-item">
                                <Select
                                    id="location"
                                    options={locationOptions}
                                    value={selectedLocationOption}
                                    onChange={selectedOption =>
                                        setFormData(prev => ({
                                            ...prev,
                                            locationId: selectedOption ? selectedOption.value : null
                                        }))
                                    }
                                    styles={customStyles}
                                    placeholder={
                                        locationsLoading
                                            ? t.placeholderLocationLoading
                                            : t.placeholderLocation
                                    }
                                    isClearable
                                    isDisabled={locationsLoading}
                                    noOptionsMessage={() => t.noLocations}
                                />
                            </div>
                        </div>
                    )}

                    <div className="form-section">
                        <div className="form-section-title">
                                <span className="form-section-title-text">
                                    {isSuperUser
                                    ? t.labelDurationPlan
                                    : t.labelRequestedDuration}
                                </span>
                        </div>
                        <div className="form-item">
                            <input
                                type="number"
                                min="1"
                                className="add-column"
                                placeholder={isSuperUser
                                    ? t.placeholderDurationPlan
                                    : t.placeholderRequestedDuration}
                                value={formData.duration_plan}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        duration_plan: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    {isSuperUser && (
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-title-text">
                                    {t.labelExpiredAt}
                                </span>
                            </div>
                            <div className="form-item">
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateTimePicker
                                        value={expiredAtValue && expiredAtValue.isValid() ? expiredAtValue : null}
                                        onChange={(nextValue) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                expired_at:
                                                    nextValue && nextValue.isValid()
                                                        ? nextValue.format('YYYY-MM-DDTHH:mm')
                                                        : ''
                                            }))
                                        }
                                        disablePast
                                        minDateTime={dayjs()}
                                        format="DD/MM/YYYY hh:mm A"
                                        slotProps={expiredAtSlotProps}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div>
                    )}

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.labelPlanRequestNote}
                            </span>
                        </div>
                        <div className="form-item">
                            <input
                                type="text"
                                className="add-column"
                                maxLength={255}
                                placeholder={t.placeholderPlanRequestNote}
                                value={formData.plan_request_note}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        plan_request_note: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    {formData.template?.id && (
                        <Fragment>
                            <div className="form-section form-section--inline">
                                <div className="form-item">
                                    <span className="form-section-icon">
                                        <DeviceIdIcon />
                                    </span>
                                    <label className="form-lbl">
                                        {t.labelTemplateId}
                                    </label>
                                    <input
                                        type="text"
                                        className="add-column"
                                        placeholder={t.placeholderTemplateId}
                                        value={formData.template?.tid || ''}
                                        readOnly
                                    />
                                </div>

                                <div className="form-item">
                                    <span className="form-section-icon">
                                        <OrgNameIcon />
                                    </span>
                                    <label className="form-lbl">
                                        {t.labelOrganizationName}
                                    </label>
                                    <input
                                        type="text"
                                        className="add-column"
                                        placeholder={
                                            t.placeholderOrganizationName
                                        }
                                        value={org.name}
                                        readOnly
                                    />
                                </div>

                                <div className="form-item">
                                    <span className="form-section-icon">
                                        <NameIcon />
                                    </span>
                                    <label className="form-lbl">
                                        {t.labelOwnerName}
                                    </label>
                                    <input
                                        type="text"
                                        className="add-column"
                                        placeholder={t.placeholderOwnerName}
                                        value={user.name}
                                        readOnly
                                    />
                                </div>
                            </div>

                            {formData.template?.icon && (
                                <img
                                    width={200}
                                    height={200}
                                    alt="icon"
                                    src={STATIC_ICONS + formData.template.icon}
                                    className="dashboard-template-icon"
                                />
                            )}
                        </Fragment>
                    )}
                </div>

                <div className="modal-footer dashboard-footer">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setState(false)}
                    >
                        {t.buttonCancel}
                    </button>
                    <button
                        className="btn btn-apply dashboard-primary-btn"
                        onClick={createDevice}
                    >
                        {t.buttonCreate}
                    </button>
                </div>
            </div>
        </div>
    );
};
